import { useEffect, useState, type FormEvent } from 'react';
import { Link, Navigate, useLocation } from 'react-router-dom';
import { z } from 'zod';
import { supabase } from '../lib/supabase.ts';
import { env } from '../lib/env.ts';
import { useAuth } from '../lib/auth.tsx';

const emailSchema = z.string().email();

type Status = { kind: 'idle' } | { kind: 'sending' } | { kind: 'sent' } | { kind: 'error'; message: string };

// Dev-only sign-in: when both env vars are set AND we are in a dev build,
// surface a one-click button that calls signInWithPassword. The whole branch
// is tree-shaken in prod because import.meta.env.DEV becomes the literal
// `false` after `vite build`.
const DEV_LOGIN_AVAILABLE =
  import.meta.env.DEV &&
  Boolean(env.VITE_DEV_LOGIN_EMAIL) &&
  Boolean(env.VITE_DEV_LOGIN_PASSWORD);

export function Login() {
  const { session, loading } = useAuth();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<Status>({ kind: 'idle' });

  // Detect Supabase magic-link callback errors surfaced in the URL hash.
  useEffect(() => {
    if (location.hash.includes('error')) {
      const params = new URLSearchParams(location.hash.slice(1));
      const description = params.get('error_description') ?? 'Authentication failed';
      setStatus({ kind: 'error', message: description });
    }
  }, [location.hash]);

  if (!loading && session) {
    return <Navigate to="/app" replace />;
  }

  async function onSubmit(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    const parsed = emailSchema.safeParse(email.trim());
    if (!parsed.success) {
      setStatus({ kind: 'error', message: 'Enter a valid email address' });
      return;
    }
    setStatus({ kind: 'sending' });
    const { error } = await supabase.auth.signInWithOtp({
      email: parsed.data,
      options: {
        emailRedirectTo: `${window.location.origin}/app`,
      },
    });
    if (error) {
      setStatus({ kind: 'error', message: error.message });
      return;
    }
    setStatus({ kind: 'sent' });
  }

  async function onDevLogin(): Promise<void> {
    if (!DEV_LOGIN_AVAILABLE) return;
    setStatus({ kind: 'sending' });
    const { error } = await supabase.auth.signInWithPassword({
      email: env.VITE_DEV_LOGIN_EMAIL!,
      password: env.VITE_DEV_LOGIN_PASSWORD!,
    });
    if (error) {
      setStatus({ kind: 'error', message: `Dev login: ${error.message}` });
      return;
    }
    // onAuthStateChange will flip session and the <Navigate> below redirects.
    setStatus({ kind: 'idle' });
  }

  return (
    <div
      style={{
        background: '#070810',
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
        fontFamily: "'Outfit', sans-serif",
        color: '#F0F2FF',
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: 420,
          background: '#111425',
          border: '1px solid rgba(255,255,255,0.07)',
          borderRadius: 14,
          padding: 32,
        }}
      >
        <Link to="/" style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: '#00E87A', letterSpacing: '0.15em' }}>
          ← OTTOBID.AI
        </Link>
        <h1
          style={{
            fontFamily: "'Syne', sans-serif",
            fontSize: 28,
            fontWeight: 800,
            marginTop: 18,
            marginBottom: 6,
          }}
        >
          Sign in
        </h1>
        <p style={{ color: '#B8BDD6', fontSize: 14, marginBottom: 26, lineHeight: 1.6 }}>
          Enter your email — we'll send a magic link.
        </p>

        <form onSubmit={onSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <input
            type="email"
            required
            autoComplete="email"
            placeholder="you@company.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={status.kind === 'sending' || status.kind === 'sent'}
            style={{
              background: '#070810',
              border: '1px solid rgba(255,255,255,0.1)',
              borderRadius: 8,
              padding: '12px 14px',
              color: '#F0F2FF',
              fontFamily: "'Outfit', sans-serif",
              fontSize: 14,
              outline: 'none',
            }}
          />
          <button
            type="submit"
            disabled={status.kind === 'sending' || status.kind === 'sent'}
            style={{
              background: '#00E87A',
              color: '#030804',
              fontFamily: "'Syne', sans-serif",
              fontWeight: 700,
              fontSize: 13,
              padding: '13px 18px',
              borderRadius: 8,
              border: 'none',
              cursor: status.kind === 'sending' ? 'wait' : 'pointer',
              textTransform: 'uppercase',
              letterSpacing: '0.08em',
              opacity: status.kind === 'sent' ? 0.5 : 1,
            }}
          >
            {status.kind === 'sending' ? 'Sending…' : status.kind === 'sent' ? 'Link sent' : 'Send magic link'}
          </button>
        </form>

        {status.kind === 'sent' && (
          <p style={{ marginTop: 18, color: '#00E87A', fontSize: 13 }}>
            Check your inbox — the link signs you straight in.
          </p>
        )}
        {status.kind === 'error' && (
          <p style={{ marginTop: 18, color: '#FF6B7A', fontSize: 13 }}>{status.message}</p>
        )}

        {DEV_LOGIN_AVAILABLE && (
          <div
            style={{
              marginTop: 24,
              paddingTop: 18,
              borderTop: '1px dashed rgba(255,255,255,0.1)',
            }}
          >
            <p
              style={{
                fontFamily: "'JetBrains Mono', monospace",
                fontSize: 10,
                color: '#FFB938',
                letterSpacing: '0.15em',
                textTransform: 'uppercase',
                marginBottom: 10,
              }}
            >
              Dev only
            </p>
            <button
              type="button"
              onClick={onDevLogin}
              disabled={status.kind === 'sending'}
              data-testid="dev-login"
              style={{
                width: '100%',
                background: 'transparent',
                color: '#FFB938',
                fontFamily: "'JetBrains Mono', monospace",
                fontSize: 12,
                padding: '10px 14px',
                borderRadius: 8,
                border: '1px solid rgba(255,185,56,0.4)',
                cursor: status.kind === 'sending' ? 'wait' : 'pointer',
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
              }}
            >
              {status.kind === 'sending' ? 'Signing in…' : `Sign in as ${env.VITE_DEV_LOGIN_EMAIL}`}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
